import { useMemo } from 'react'

import {
  BrokenImageOutlined,
  CheckCircleOutlineOutlined,
  ReportProblemOutlined,
  VideocamOffOutlined,
} from '@mui/icons-material'
import { Box, LinearProgress, Typography } from '@mui/material'
import { AlertSeverityEnum, AvcTask, AvcTaskHealthEnum, AvcTaskStateEnum } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { IconButtonLink } from 'components/LinkedMUIComponents'
import { StartStopButtonWrapper } from 'domains/Workspaces/ContentFlows/AvcTasks'
import { NoSrcPlaceholderWrapper, Thumbnail } from 'domains/Workspaces/Events'
import { AlertBadge } from 'layouts'
import { avcTasksOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks'
import { avcTaskMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask'
import { formatDuration, getAlertSeverityColor, getAvcTaskStateColor } from 'utils'

const loadingStates = new Set<Partial<AvcTaskStateEnum>>([
  AvcTaskStateEnum.Starting,
  AvcTaskStateEnum.Pending,
  AvcTaskStateEnum.Stopping,
  AvcTaskStateEnum.Provisioning,
  AvcTaskStateEnum.Retrying,
])

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  return useMemo<ColumnDef<AvcTask>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const { src, alt } = getValue<{ src?: string; alt: string }>()
          if (src) {
            return (
              <picture>
                <Thumbnail type="image/jpeg" data={src} aria-label={alt}>
                  <NoSrcPlaceholderWrapper>
                    <BrokenImageOutlined />
                  </NoSrcPlaceholderWrapper>
                </Thumbnail>
              </picture>
            )
          }
          return (
            <NoSrcPlaceholderWrapper>
              <VideocamOffOutlined />
            </NoSrcPlaceholderWrapper>
          )
        },
      },
      {
        accessorKey: 'name',
        header: t('name'),
        meta: {
          sizing: {
            minWidth: 260,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => ({ duration: row.status?.executionDuration, state: row.state }),
        id: '_run_duration',
        header: t('domain:Workspace.AvcTask.runDuration'),
        meta: {
          sizing: {
            minWidth: 160,
          },
          align: 'end',
        },
        cell: (cell) => {
          const { duration, state } = cell.getValue<{ duration?: number; state: AvcTaskStateEnum }>()
          const isPending = loadingStates.has(state)

          if (duration) {
            return (
              <Box>
                <Typography variant="inherit">
                  {(
                    formatDuration(
                      duration,
                      { seconds: true, minutes: true, hours: true, days: true },
                      { suffixed: true, omitEmptyValues: true },
                    ) as string[]
                  ).join(', ')}
                </Typography>
                <LinearProgress
                  sx={{ width: '100%' }}
                  variant={isPending ? 'indeterminate' : 'determinate'}
                  value={isPending ? 50 : 100}
                />
              </Box>
            )
          }
          return (
            <Typography variant="inherit" noWrap>
              {t('nA.long')}
            </Typography>
          )
        },
      },
      {
        accessorFn: (row) => ({ state: row.state, health: row.health }),
        header: t('domain:Workspace.AvcTask.status'),
        meta: {
          sizing: {
            width: 180,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const { state, health } = cell.getValue<{ state: AvcTaskStateEnum; health: AvcTaskHealthEnum }>()

          return (
            <Typography
              sx={[
                {
                  width: 120,
                  textAlign: 'center',
                  display: 'inline-block',
                  px: 0.5,
                  py: 0.3,
                  m: 0,
                  backgroundColor: (theme) => getAvcTaskStateColor(state, theme.palette.mode, health),
                  color: (theme) =>
                    theme.palette.getContrastText(getAvcTaskStateColor(state, theme.palette.mode, health)),
                  borderRadius: 1,
                },
              ]}
              variant="text-sm"
            >
              {upperFirst(state.toLowerCase())}
            </Typography>
          )
        },
      },
      {
        accessorKey: 'alerts',
        header: t('domain:Workspace.AvcTask.alert', { count: 2 }),
        meta: {
          sizing: {
            width: 64,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const alerts = cell.getValue<AvcTask['alerts']>()
          const severityLevel = alerts.some((item) => item.severity === AlertSeverityEnum.High)
            ? AlertSeverityEnum.High
            : AlertSeverityEnum.Medium
          return alerts.length > 0 ? (
            <AlertBadge
              badgeContent={alerts.length}
              color={severityLevel === AlertSeverityEnum.High ? 'error' : 'warning'}
              overlap="circular"
            >
              <IconButtonLink
                size="small"
                from={avcTasksOverviewRoute.fullPath}
                to={avcTaskMonitoringRoute.to}
                params={{ avcTaskId: cell.row.original.id }}
              >
                <ReportProblemOutlined
                  sx={{
                    color: (theme) => getAlertSeverityColor(severityLevel, theme.palette.mode),
                  }}
                />
              </IconButtonLink>
            </AlertBadge>
          ) : (
            <CheckCircleOutlineOutlined
              sx={{
                color: (theme) => theme.palette.success[theme.palette.mode],
              }}
            />
          )
        },
      },
      {
        accessorFn: (row) => row.config?.executor?.instanceIds ?? [],
        id: '_executor',
        header: t('domain:Workspace.AvcTask.noExec'),
        meta: {
          sizing: {
            width: 80,
          },
          align: 'center',
        },
        cell: (cell) => {
          const instanceIds = cell.getValue<string[]>()
          return (
            <Typography variant="inherit" noWrap>
              {instanceIds.length || 0}
            </Typography>
          )
        },
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
      {
        accessorFn: (row) => ({ id: row.id, state: row.state }),
        id: '_start_stop',
        header: () => null,
        meta: {
          sizing: {
            width: 144,
            px: 2,
          },
        },
        cell: (cell) => {
          return <StartStopButtonWrapper {...cell.getValue<Pick<AvcTask, 'id' | 'state'>>()} />
        },
      },
    ],
    [t],
  )
}
